import { apiClient } from '@/services/apiClient'
import type { ApprovedAsset, PersonaAssetGrants } from '@/types/assets'

export function transformAsset(p: Record<string, unknown>): ApprovedAsset {
  return {
    id: p.id as string,
    userId: p.user_id as string,
    name: p.name as string,
    slug: p.slug as string,
    description: (p.description as string | null) ?? null,
    tags: (p.tags as string[] | null) ?? [],
    mediaRef: p.media_ref as string,
    mimeType: p.mime_type as string,
    sizeBytes: (p.size_bytes as number) ?? 0,
    sha256: p.sha256 as string,
    isActive: (p.is_active as boolean) ?? true,
    sortOrder: (p.sort_order as number) ?? 0,
    createdAt: (p.created_at as string | null) ?? null,
    updatedAt: (p.updated_at as string | null) ?? null,
    kind: ((p.kind as string) === 'url' ? 'url' : 'file'),
    url: (p.url as string | null) ?? null,
  }
}

export async function listAssets(): Promise<ApprovedAsset[]> {
  const res = await apiClient.fetch('/api/assets')
  if (!res.ok) return []
  const data = await res.json()
  return (data.items || []).map(transformAsset)
}

export async function uploadAsset(
  file: File, name: string, slug: string, description: string,
  tags: string[] = [],
): Promise<Response> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('name', name)
  fd.append('slug', slug)
  if (description) fd.append('description', description)
  if (tags.length > 0) fd.append('tags', JSON.stringify(tags))
  return apiClient.fetch('/api/assets', { method: 'POST', body: fd })
}

export async function createUrlAsset(body: {
  name: string; slug: string; url: string; description?: string; tags?: string[]
}): Promise<Response> {
  return apiClient.fetch('/api/assets/url', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function updateAsset(id: string, body: Record<string, unknown>): Promise<Response> {
  return apiClient.fetch(`/api/assets/${id}`, { method: 'PUT', body: JSON.stringify(body) })
}

export async function deleteAsset(id: string): Promise<Response> {
  return apiClient.fetch(`/api/assets/${id}`, { method: 'DELETE' })
}

export async function getPersonaAssetGrants(personaId: string): Promise<PersonaAssetGrants> {
  const res = await apiClient.fetch(`/api/personas/${personaId}/asset-grants`)
  if (!res.ok) return { grantAll: false, assetIds: [] }
  const d = await res.json()
  return { grantAll: !!d.grant_all, assetIds: (d.asset_ids as string[]) ?? [] }
}

export async function setPersonaAssetGrants(
  personaId: string, grants: PersonaAssetGrants,
): Promise<Response> {
  return apiClient.fetch(`/api/personas/${personaId}/asset-grants`, {
    method: 'PUT',
    body: JSON.stringify({ grant_all: grants.grantAll, asset_ids: grants.assetIds }),
  })
}
